import React, { useState, useEffect, useMemo } from "react";
import {
  StyleSheet,
  View,
  Modal,
  TouchableOpacity,
  Pressable,
  ScrollView,
  Platform,
  TextInput,
} from "react-native";
import Svg, { Circle, Line } from "react-native-svg";
import { Ionicons } from "@expo/vector-icons";
import {
  IDate,
  ISingleDate,
  DateType,
  DateDurationUnit,
  createSingleDate,
  parseDate,
  serializeDate,
  formatSingleDate,
  formatDate,
  getDaysInMonth,
  MONTH_ABBREVIATIONS,
  compareSingleDates,
  parseDuration,
  formatDuration,
  calculateToDateFromDuration,
} from "common247";
import { ThemedText } from "@/components/ThemedText";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedSegmentedControl } from "@/components/ThemedSegmentedControl";
import { ThemedToggle } from "@/components/ThemedToggle";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ThemedPopupScreen } from "@/components/ThemedPopupScreen";
import {
  calculatePopoverPosition,
  type IPopoverAnchor,
  type IPopoverLayout,
} from "@/utils/popover-positioning";

/**
 * Props for the DateSelector modal dialog.
 */
export interface IDateSelectorProps {
  /**
   * Whether the selector modal is visible.
   */
  visible: boolean;

  /**
   * Callback to close the selector modal.
   */
  onClose: () => void;

  /**
   * Current date value (payload, string, or null).
   */
  value?: IDate | string | null;

  /**
   * Callback fired when the user applies the picked date/time/range.
   */
  onApply: (payload: IDate, jsonString: string) => void;

  /**
   * Context type for the DateSelector: "Date" (single date using From date) or "Range" (From & To dates).
   * Defaults to "Date". Only in the Memory Editor does the Date need to be a "Range".
   */
  type?: DateType;

  /**
   * Optional custom title for the selector modal.
   */
  title?: string;

  /**
   * Optional contextual anchor position (e.g. cursor or trigger coordinates).
   */
  anchorPosition?: IPopoverAnchor | null;
}


type ClockDialMode = "hours" | "minutes";

const CLOCK_DIAMETER = 116;
const CLOCK_RADIUS = CLOCK_DIAMETER / 2;
const NUMBERS_RADIUS = 40;
const HAND_RADIUS = 36;

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES_5 = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

/**
 * Universal DateSelector component for all date fields across the application.
 * In the JSON object, single dates are stored in the From Date (`from`).
 * Defaults to type "Date", using the From date. When type is "Range", uses both From and To dates.
 */
export function DateSelector({
  visible,
  onClose,
  value,
  onApply,
  type = "Date",
  title,
  anchorPosition,
}: IDateSelectorProps) {
  const tintColor = useThemeColor({}, "tint");
  const dangerColor = useThemeColor({}, "danger");
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textMuted = useThemeColor({}, "textMuted");
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const readOnlyBg = useThemeColor({}, "inputReadOnly");

const DURATION_PRESETS: { label: string; value: string }[] = [
  { label: "30m", value: "30 mins" },
  { label: "1h", value: "1 hour" },
  { label: "2h", value: "2 hours" },
  { label: "4h", value: "4 hours" },
  { label: "1d", value: "1 day" },
  { label: "3d", value: "3 days" },
  { label: "1w", value: "1 week" },
  { label: "2w", value: "2 weeks" },
  { label: "1mo", value: "1 month" },
  { label: "3mo", value: "3 months" },
  { label: "1yr", value: "1 year" },
];

  const [layout, setLayout] = useState<IPopoverLayout>(() =>
    calculatePopoverPosition({
      anchorPosition,
      popoverWidth: 360,
      popoverHeight: 380,
    })
  );

  useEffect(() => {
    if (!visible) return;
    const updateLayout = () => {
      setLayout(
        calculatePopoverPosition({
          anchorPosition,
          popoverWidth: 360,
          popoverHeight: 380,
        })
      );
    };
    updateLayout();
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.addEventListener("resize", updateLayout);
      return () => {
        window.removeEventListener("resize", updateLayout);
      };
    }
  }, [visible, anchorPosition]);

  // Determine effective context type
  const effectiveType: DateType = type || "Date";

  // Initial normalized payload
  const initialPayload = useMemo(() => parseDate(value), [value]);

  const [pickerMode, setPickerMode] = useState<DateType>(effectiveType);
  const [rangeEndType, setRangeEndType] = useState<"to" | "duration">(() =>
    initialPayload.duration ? "duration" : "to"
  );
  const [duration, setDuration] = useState<string | undefined>(() => initialPayload.duration);
  const [activeTarget, setActiveTarget] = useState<"from" | "to" | "duration">(() =>
    initialPayload.duration ? "duration" : "from"
  );
  const [customDurationVal, setCustomDurationVal] = useState<string>(() => {
    if (initialPayload.duration) {
      const p = parseDuration(initialPayload.duration);
      if (p) return String(p.value);
    }
    return "1";
  });
  const [customDurationUnit, setCustomDurationUnit] = useState<DateDurationUnit>(() => {
    if (initialPayload.duration) {
      const p = parseDuration(initialPayload.duration);
      if (p) return p.unit;
    }
    return "hours";
  });

  // Internal date objects: From date is the primary date for both Date and Range
  const [fromDate, setFromDate] = useState<ISingleDate>(() => {
    return (
      initialPayload.from ||
      createSingleDate(
        "exact_date",
        new Date().getFullYear(),
        new Date().getMonth() + 1,
        new Date().getDate()
      )
    );
  });

  const [toDate, setToDate] = useState<ISingleDate>(() => {
    return (
      initialPayload.to ||
      createSingleDate(
        "exact_date",
        new Date().getFullYear(),
        new Date().getMonth() + 1,
        new Date().getDate() + 1
      )
    );
  });

  // Sync state when opened with new value
  useEffect(() => {
    if (visible) {
      const parsed = parseDate(value);
      const targetType: DateType = type || parsed.type || "Date";
      setPickerMode(targetType);
      if (parsed.from || parsed.single) {
        setFromDate(parsed.from || parsed.single!);
      }
      if (parsed.to) {
        setToDate(parsed.to);
      }
      if (parsed.duration) {
        setDuration(parsed.duration);
        setRangeEndType("duration");
        setActiveTarget("duration");
        const p = parseDuration(parsed.duration);
        if (p) {
          setCustomDurationVal(String(p.value));
          setCustomDurationUnit(p.unit);
        }
      } else {
        setDuration(undefined);
        setRangeEndType("to");
        setActiveTarget("from");
      }
    }
  }, [visible, value, type]);

  // Dynamically calculate the To date whenever duration or fromDate changes
  const calculatedTo = useMemo(() => {
    if (rangeEndType === "duration" && duration) {
      return calculateToDateFromDuration(fromDate, duration);
    }
    return toDate;
  }, [rangeEndType, duration, fromDate, toDate]);

  // Current active working date item (fromDate for single date, from target, or duration target)
  const currentItem =
    pickerMode === "Date"
      ? fromDate
      : activeTarget === "to"
      ? toDate
      : fromDate;

  // Calendar browsing month/year
  const [viewYear, setViewYear] = useState<number>(
    currentItem.year || new Date().getFullYear()
  );
  const [viewMonth, setViewMonth] = useState<number>(
    currentItem.month || new Date().getMonth() + 1
  );

  useEffect(() => {
    if (currentItem.year) setViewYear(currentItem.year);
    if (currentItem.month) setViewMonth(currentItem.month);
  }, [currentItem.year, currentItem.month, activeTarget]);

  // Clock dial mode
  const [clockMode, setClockMode] = useState<ClockDialMode>("hours");

  // Parsed time values for the active date item
  const parsedTime = useMemo(() => {
    const rawTime = currentItem.time || "00:00";
    const [hStr, mStr] = rawTime.split(":");
    const h24 = parseInt(hStr || "0", 10);
    const mins = parseInt(mStr || "0", 10);
    const isPM = h24 >= 12;
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return { h24, h12, minutes: mins, isPM };
  }, [currentItem.time]);

  // Modifies currently active date component (from or to)
  const updateCurrentItem = (updates: Partial<ISingleDate>) => {
    const target = pickerMode === "Date" ? "from" : activeTarget === "to" ? "to" : "from";
    const base = target === "from" ? fromDate : toDate;
    const merged: ISingleDate = {
      ...base,
      ...updates,
      dateStr: "",
    };
    merged.dateStr = formatSingleDate(merged);

    if (target === "from") {
      setFromDate(merged);
    } else {
      setToDate(merged);
    }
  };

  // Month navigation
  const changeMonth = (delta: number) => {
    let nextMonth = viewMonth + delta;
    let nextYear = viewYear;
    if (nextMonth < 1) {
      nextMonth = 12;
      nextYear -= 1;
    } else if (nextMonth > 12) {
      nextMonth = 1;
      nextYear += 1;
    }
    setViewMonth(nextMonth);
    setViewYear(nextYear);
  };

  // Calendar cell builder
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(viewYear, viewMonth - 1, 1).getDay();
    // In JS Sunday=0, convert Monday=0
    const startOffset = (firstDayOfMonth + 6) % 7;
    const daysInCurrMonth = getDaysInMonth(viewYear, viewMonth);
    const prevMonthDays = getDaysInMonth(
      viewMonth === 1 ? viewYear - 1 : viewYear,
      viewMonth === 1 ? 12 : viewMonth - 1
    );

    const cells: { day: number; isCurrentMonth: boolean }[] = [];

    // Leading days from previous month
    for (let i = startOffset - 1; i >= 0; i--) {
      cells.push({ day: prevMonthDays - i, isCurrentMonth: false });
    }

    // Current month days
    for (let d = 1; d <= daysInCurrMonth; d++) {
      cells.push({ day: d, isCurrentMonth: true });
    }

    // Trailing days to fill 35 or 42 grid
    const totalCells = cells.length > 35 ? 42 : 35;
    let nextDay = 1;
    while (cells.length < totalCells) {
      cells.push({ day: nextDay++, isCurrentMonth: false });
    }

    return cells;
  }, [viewYear, viewMonth]);

  /**
   * Applies the current picked date and notifies parent callback.
   * If type is "Date", uses the From date.
   * If type is "Range", uses the From and To dates.
   */
  const handleApply = () => {
    const finalType: DateType = pickerMode;
    const isRange = finalType === "Range" || finalType === "range";
    const hasDuration = isRange && rangeEndType === "duration" && Boolean(duration);
    const effectiveTo = hasDuration ? calculatedTo : toDate;
    const finalPayload: IDate = {
      type: finalType,
      mode: isRange ? "range" : "single",
      from: fromDate,
      single: fromDate,
      to: isRange ? effectiveTo : undefined,
      duration: hasDuration ? duration : undefined,
      formatted: "",
    };
    finalPayload.formatted = formatDate(finalPayload);
    const jsonStr = serializeDate(finalPayload);
    onApply(finalPayload, jsonStr);
    onClose();
  };

  const isRangeInverted =
    pickerMode === "Range" &&
    rangeEndType === "to" &&
    compareSingleDates(fromDate, toDate) > 0;

  // Selected hour angle
  const hourAngle = ((parsedTime.h12 % 12) * 30 - 90) * (Math.PI / 180);
  const hourHandX = CLOCK_RADIUS + HAND_RADIUS * Math.cos(hourAngle);
  const hourHandY = CLOCK_RADIUS + HAND_RADIUS * Math.sin(hourAngle);

  // Selected minute angle
  const minuteAngle = (parsedTime.minutes * 6 - 90) * (Math.PI / 180);
  const minuteHandX = CLOCK_RADIUS + HAND_RADIUS * Math.cos(minuteAngle);
  const minuteHandY = CLOCK_RADIUS + HAND_RADIUS * Math.sin(minuteAngle);

  const activeHandX = clockMode === "hours" ? hourHandX : minuteHandX;
  const activeHandY = clockMode === "hours" ? hourHandY : minuteHandY;

  const resolvedTitle =
    title || (pickerMode === "Range" ? "Pick Date Range" : "Pick Date");

  return (
    <ThemedPopupScreen
      visible={visible}
      onClose={onClose}
      anchorPosition={anchorPosition}
      title={resolvedTitle}
      icon="calendar-outline"
      width={layout.width}
      maxHeight={layout.maxHeight}
      cardStyle={{ backgroundColor: cardBg, borderColor }}
      testID="date-picker"
      footer={
        <View style={styles.modalFooter}>
          <ThemedButton
            title="Cancel"
            variant="secondary"
            onPress={onClose}
            style={styles.actionBtn}
          />
          <ThemedButton
            title="Apply"
            variant="primary"
            onPress={handleApply}
            style={styles.actionBtn}
            disabled={isRangeInverted}
          />
        </View>
      }
    >
      <ScrollView style={styles.scrollBody} contentContainerStyle={styles.scrollContent}>
            {/* Mode selection only in Range-enabled contexts (e.g. MemoryEditor) */}
            {effectiveType === "Range" && (
              <View style={styles.modeRow}>
                <ThemedSegmentedControl
                  value={pickerMode}
                  onChange={(m) => {
                    const modeVal = m === "range" || m === "Range" ? "Range" : "Date";
                    setPickerMode(modeVal);
                    setActiveTarget("from");
                  }}
                  options={[
                    { label: "Single Date", value: "Date" },
                    { label: "Date Range", value: "Range" },
                  ]}
                />
              </View>
            )}

            {/* In Range mode: End Target Type toggle (End Date vs Duration) */}
            {pickerMode === "Range" && (
              <View style={styles.rangeEndTypeRow}>
                <ThemedSegmentedControl
                  value={rangeEndType}
                  onChange={(val) => {
                    const mode = val as "to" | "duration";
                    setRangeEndType(mode);
                    if (mode === "duration") {
                      setActiveTarget("duration");
                      if (!duration) {
                        const defDur = fromDate.time ? "2 hours" : "1 day";
                        setDuration(defDur);
                        const p = parseDuration(defDur);
                        if (p) {
                          setCustomDurationVal(String(p.value));
                          setCustomDurationUnit(p.unit);
                        }
                      }
                    } else {
                      setActiveTarget("to");
                    }
                  }}
                  options={[
                    { label: "End Date", value: "to" },
                    { label: "Duration", value: "duration" },
                  ]}
                />
              </View>
            )}

            {/* Target sub-tabs for Range mode */}
            {pickerMode === "Range" && (
              <View style={styles.rangeTargetRow}>
                <TouchableOpacity
                  style={[
                    styles.targetTabBtn,
                    activeTarget === "from" && [
                      styles.targetTabBtnActive,
                      { borderColor: tintColor, backgroundColor: badgeBg },
                    ],
                    { borderColor },
                  ]}
                  onPress={() => setActiveTarget("from")}
                >
                  <ThemedText style={[styles.targetLabel, { color: textMuted }]}>FROM</ThemedText>
                  <ThemedText
                    type="defaultSemiBold"
                    style={[styles.targetValue, activeTarget === "from" && { color: tintColor }]}
                  >
                    {fromDate.dateStr || "Set start"}
                  </ThemedText>
                </TouchableOpacity>

                <Ionicons name="arrow-forward" size={14} color={textMuted} style={{ marginHorizontal: 2 }} />

                {rangeEndType === "to" ? (
                  <TouchableOpacity
                    style={[
                      styles.targetTabBtn,
                      activeTarget === "to" && [
                        styles.targetTabBtnActive,
                        { borderColor: tintColor, backgroundColor: badgeBg },
                      ],
                      { borderColor },
                    ]}
                    onPress={() => setActiveTarget("to")}
                  >
                    <ThemedText style={[styles.targetLabel, { color: textMuted }]}>TO</ThemedText>
                    <ThemedText
                      type="defaultSemiBold"
                      style={[styles.targetValue, activeTarget === "to" && { color: tintColor }]}
                    >
                      {toDate.dateStr || "Set end"}
                    </ThemedText>
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity
                    style={[
                      styles.targetTabBtn,
                      activeTarget === "duration" && [
                        styles.targetTabBtnActive,
                        { borderColor: tintColor, backgroundColor: badgeBg },
                      ],
                      { borderColor },
                    ]}
                    onPress={() => setActiveTarget("duration")}
                  >
                    <ThemedText style={[styles.targetLabel, { color: textMuted }]}>DURATION</ThemedText>
                    <ThemedText
                      type="defaultSemiBold"
                      style={[styles.targetValue, activeTarget === "duration" && { color: tintColor }]}
                    >
                      {duration || "Set duration"}
                    </ThemedText>
                  </TouchableOpacity>
                )}
              </View>
            )}

            {/* Inverted range alert */}
            {isRangeInverted && (
              <View style={[styles.alertBanner, { borderColor: dangerColor }]}>
                <Ionicons name="alert-circle" size={14} color={dangerColor} />
                <ThemedText style={[styles.alertBannerText, { color: dangerColor }]}>
                  Start date cannot be after end date
                </ThemedText>
              </View>
            )}

            {/* DURATION SELECTION PANEL vs UNIFIED DATE & TIME PANEL */}
            {activeTarget === "duration" ? (
              <View style={styles.durationPanel}>
                <ThemedText style={[styles.durationSectionTitle, { color: textMuted }]}>
                  QUICK PRESETS
                </ThemedText>
                <View style={styles.durationChipsRow}>
                  {DURATION_PRESETS.map((p) => {
                    const isSel = duration === p.value;
                    return (
                      <TouchableOpacity
                        key={p.value}
                        style={[
                          styles.durationChip,
                          { borderColor },
                          isSel && [
                            styles.durationChipSelected,
                            { backgroundColor: tintColor, borderColor: tintColor },
                          ],
                        ]}
                        onPress={() => {
                          setDuration(p.value);
                          const parsed = parseDuration(p.value);
                          if (parsed) {
                            setCustomDurationVal(String(parsed.value));
                            setCustomDurationUnit(parsed.unit);
                          }
                        }}
                      >
                        <ThemedText
                          style={[
                            styles.durationChipText,
                            isSel && { color: "#FFFFFF", fontWeight: "700" },
                          ]}
                        >
                          {p.label}
                        </ThemedText>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                <ThemedText style={[styles.durationSectionTitle, { color: textMuted, marginTop: 8 }]}>
                  CUSTOM DURATION
                </ThemedText>
                <View style={styles.customDurationRow}>
                  <TextInput
                    value={customDurationVal}
                    onChangeText={(t) => {
                      setCustomDurationVal(t);
                      const n = parseFloat(t);
                      if (!isNaN(n) && n > 0) {
                        setDuration(formatDuration({ value: n, unit: customDurationUnit }));
                      }
                    }}
                    keyboardType="numeric"
                    style={[styles.durationInput, { borderColor, color: "#222", backgroundColor: cardBg }]}
                  />
                  <View style={styles.durationUnitChipsRow}>
                    {(["minutes", "hours", "days", "weeks", "months", "years"] as DateDurationUnit[]).map((u) => {
                      const isSel = customDurationUnit === u;
                      const abbrev =
                        u === "minutes"
                          ? "mins"
                          : u === "hours"
                          ? "hrs"
                          : u === "months"
                          ? "mos"
                          : u === "years"
                          ? "yrs"
                          : u;
                      return (
                        <TouchableOpacity
                          key={u}
                          style={[
                            styles.durationUnitChip,
                            { borderColor },
                            isSel && [
                              styles.durationUnitChipSelected,
                              { backgroundColor: badgeBg, borderColor: tintColor },
                            ],
                          ]}
                          onPress={() => {
                            setCustomDurationUnit(u);
                            const n = parseFloat(customDurationVal) || 1;
                            setDuration(formatDuration({ value: n, unit: u }));
                          }}
                        >
                          <ThemedText
                            style={[
                              styles.durationUnitChipText,
                              isSel && { color: tintColor, fontWeight: "700" },
                            ]}
                          >
                            {abbrev}
                          </ThemedText>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>

                {/* Calculated End Date Banner */}
                <View style={[styles.calculatedEndBanner, { backgroundColor: badgeBg, borderColor }]}>
                  <Ionicons name="time-outline" size={14} color={tintColor} />
                  <ThemedText style={[styles.calculatedEndText, { color: tintColor }]}>
                    Calculated End: <ThemedText type="defaultSemiBold">{calculatedTo.dateStr || "—"}</ThemedText>
                  </ThemedText>
                </View>
              </View>
            ) : (
              /* UNIFIED DATE & TIME PANEL */
              <View style={styles.dateAndTimePanel}>
              {/* DATE COLUMN (Compact Calendar) */}
              <View style={styles.calendarCol}>
                {/* Month navigation header */}
                <View style={styles.monthNavRow}>
                  <TouchableOpacity onPress={() => changeMonth(-1)} style={styles.monthNavArrow} activeOpacity={0.7}>
                    <Ionicons name="chevron-back" size={16} color={tintColor} />
                  </TouchableOpacity>

                  <ThemedText type="defaultSemiBold" style={styles.monthNavTitle}>
                    {MONTH_ABBREVIATIONS[viewMonth - 1]} {viewYear}
                  </ThemedText>

                  <TouchableOpacity onPress={() => changeMonth(1)} style={styles.monthNavArrow} activeOpacity={0.7}>
                    <Ionicons name="chevron-forward" size={16} color={tintColor} />
                  </TouchableOpacity>
                </View>

                {/* Weekday headers */}
                <View style={styles.weekdayRow}>
                  {WEEKDAYS.map((w, idx) => (
                    <ThemedText key={idx} style={[styles.weekdayHeader, { color: textMuted }]}>
                      {w}
                    </ThemedText>
                  ))}
                </View>

                {/* Calendar Days Grid */}
                <View style={styles.calendarGrid}>
                  {calendarDays.map((cell, idx) => {
                    const isSelected =
                      cell.isCurrentMonth &&
                      currentItem.year === viewYear &&
                      currentItem.month === viewMonth &&
                      currentItem.day === cell.day;

                    return (
                      <TouchableOpacity
                        key={idx}
                        style={[
                          styles.dayCell,
                          isSelected && [styles.dayCellSelected, { backgroundColor: tintColor }],
                        ]}
                        onPress={() => {
                          if (!cell.isCurrentMonth) {
                            return;
                          }
                          updateCurrentItem({
                            year: viewYear,
                            month: viewMonth,
                            day: cell.day,
                            precision: currentItem.time ? "exact_datetime" : "exact_date",
                          });
                        }}
                        activeOpacity={0.7}
                      >
                        <ThemedText
                          style={[
                            styles.dayCellText,
                            !cell.isCurrentMonth && { opacity: 0.25 },
                            isSelected && styles.dayCellTextSelected,
                          ]}
                        >
                          {cell.day}
                        </ThemedText>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                {/* Quick year adjuster */}
                <View style={styles.yearAdjustRow}>
                  <TouchableOpacity
                    onPress={() => setViewYear(viewYear - 1)}
                    style={[styles.yearChip, { borderColor }]}
                  >
                    <ThemedText style={styles.yearChipText}>-1 Yr</ThemedText>
                  </TouchableOpacity>

                  <ThemedText style={[styles.currentYearText, { color: textMuted }]}>
                    Year: {viewYear}
                  </ThemedText>

                  <TouchableOpacity
                    onPress={() => setViewYear(viewYear + 1)}
                    style={[styles.yearChip, { borderColor }]}
                  >
                    <ThemedText style={styles.yearChipText}>+1 Yr</ThemedText>
                  </TouchableOpacity>
                </View>
              </View>

              {/* CLOCK COLUMN (Analog Clock Dial) */}
              <View style={[styles.clockCol, { borderColor }]}>
                {/* Time Display & Stage Switcher */}
                <View style={styles.timeHeaderRow}>
                  <TouchableOpacity
                    onPress={() => setClockMode("hours")}
                    style={[
                      styles.timeDigitBox,
                      clockMode === "hours" && [styles.timeDigitBoxActive, { borderColor: tintColor, backgroundColor: badgeBg }],
                      { borderColor },
                    ]}
                  >
                    <ThemedText
                      type="defaultSemiBold"
                      style={[styles.timeDigitText, clockMode === "hours" && { color: tintColor }]}
                    >
                      {parsedTime.h12 < 10 ? `0${parsedTime.h12}` : parsedTime.h12}
                    </ThemedText>
                  </TouchableOpacity>

                  <ThemedText style={styles.timeColon}>:</ThemedText>

                  <TouchableOpacity
                    onPress={() => setClockMode("minutes")}
                    style={[
                      styles.timeDigitBox,
                      clockMode === "minutes" && [styles.timeDigitBoxActive, { borderColor: tintColor, backgroundColor: badgeBg }],
                      { borderColor },
                    ]}
                  >
                    <ThemedText
                      type="defaultSemiBold"
                      style={[styles.timeDigitText, clockMode === "minutes" && { color: tintColor }]}
                    >
                      {parsedTime.minutes < 10 ? `0${parsedTime.minutes}` : parsedTime.minutes}
                    </ThemedText>
                  </TouchableOpacity>

                  {/* AM / PM Segment */}
                  <View style={styles.amPmCol}>
                    <TouchableOpacity
                      onPress={() => {
                        if (parsedTime.isPM) {
                          const newH24 = parsedTime.h24 - 12;
                          const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
                          updateCurrentItem({ time: `${pad(newH24)}:${pad(parsedTime.minutes)}` });
                        }
                      }}
                      style={[
                        styles.amPmBtn,
                        !parsedTime.isPM && [styles.amPmBtnActive, { backgroundColor: tintColor }],
                      ]}
                    >
                      <ThemedText style={[styles.amPmText, !parsedTime.isPM && styles.amPmTextActive]}>
                        AM
                      </ThemedText>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => {
                        if (!parsedTime.isPM) {
                          const newH24 = parsedTime.h24 + 12;
                          const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
                          updateCurrentItem({ time: `${pad(newH24)}:${pad(parsedTime.minutes)}` });
                        }
                      }}
                      style={[
                        styles.amPmBtn,
                        parsedTime.isPM && [styles.amPmBtnActive, { backgroundColor: tintColor }],
                      ]}
                    >
                      <ThemedText style={[styles.amPmText, parsedTime.isPM && styles.amPmTextActive]}>
                        PM
                      </ThemedText>
                    </TouchableOpacity>
                  </View>
                </View>

                {/* SVG Analog Clock Dial */}
                <View style={styles.clockDialWrap}>
                  <Svg width={CLOCK_DIAMETER} height={CLOCK_DIAMETER}>
                    {/* Clock face circle */}
                    <Circle
                      cx={CLOCK_RADIUS}
                      cy={CLOCK_RADIUS}
                      r={CLOCK_RADIUS - 2}
                      fill={readOnlyBg}
                      stroke={borderColor}
                      strokeWidth={1.5}
                    />

                    {/* Clock Hand line */}
                    <Line
                      x1={CLOCK_RADIUS}
                      y1={CLOCK_RADIUS}
                      x2={activeHandX}
                      y2={activeHandY}
                      stroke={tintColor}
                      strokeWidth={2}
                    />

                    {/* Center pivot dot */}
                    <Circle
                      cx={CLOCK_RADIUS}
                      cy={CLOCK_RADIUS}
                      r={3.5}
                      fill={tintColor}
                    />

                    {/* Active hand tip dot */}
                    <Circle
                      cx={activeHandX}
                      cy={activeHandY}
                      r={9}
                      fill={tintColor}
                      opacity={0.3}
                    />
                  </Svg>

                  {/* Radial numbers overlay */}
                  {clockMode === "hours" ? (
                    HOURS_12.map((h) => {
                      const angle = ((h % 12) * 30 - 90) * (Math.PI / 180);
                      const x = CLOCK_RADIUS + NUMBERS_RADIUS * Math.cos(angle) - 10;
                      const y = CLOCK_RADIUS + NUMBERS_RADIUS * Math.sin(angle) - 10;
                      const isSelected = parsedTime.h12 === h;

                      return (
                        <TouchableOpacity
                          key={h}
                          style={[
                            styles.clockNumberWrap,
                            { left: x, top: y },
                            isSelected && { backgroundColor: tintColor },
                          ]}
                          onPress={() => {
                            let newH24 = h % 12;
                            if (parsedTime.isPM) newH24 += 12;
                            const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
                            updateCurrentItem({
                              time: `${pad(newH24)}:${pad(parsedTime.minutes)}`,
                              precision: "exact_datetime",
                            });
                            setClockMode("minutes");
                          }}
                        >
                          <ThemedText
                            style={[
                              styles.clockNumberText,
                              isSelected && styles.clockNumberTextSelected,
                            ]}
                          >
                            {h}
                          </ThemedText>
                        </TouchableOpacity>
                      );
                    })
                  ) : (
                    MINUTES_5.map((m) => {
                      const angle = (m * 6 - 90) * (Math.PI / 180);
                      const x = CLOCK_RADIUS + NUMBERS_RADIUS * Math.cos(angle) - 10;
                      const y = CLOCK_RADIUS + NUMBERS_RADIUS * Math.sin(angle) - 10;
                      const isSelected = parsedTime.minutes === m;

                      return (
                        <TouchableOpacity
                          key={m}
                          style={[
                            styles.clockNumberWrap,
                            { left: x, top: y },
                            isSelected && { backgroundColor: tintColor },
                          ]}
                          onPress={() => {
                            const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
                            updateCurrentItem({
                              time: `${pad(parsedTime.h24)}:${pad(m)}`,
                              precision: "exact_datetime",
                            });
                          }}
                        >
                          <ThemedText
                            style={[
                              styles.clockNumberText,
                              isSelected && styles.clockNumberTextSelected,
                            ]}
                          >
                            {m < 10 ? `0${m}` : m}
                          </ThemedText>
                        </TouchableOpacity>
                      );
                    })
                  )}
                </View>
              </View>
            </View>
            )}

            {/* "ABOUT" APPROXIMATE TOGGLE & SUMMARY BAR */}
            <View style={[styles.bottomControlBar, { borderColor }]}>
              {/* About ThemedToggle */}
              {activeTarget !== "duration" && (
                <View style={styles.approxToggleRow}>
                  <ThemedToggle
                    value={Boolean(currentItem.isApproximate)}
                    onValueChange={(val) => {
                      updateCurrentItem({
                        isApproximate: val,
                        precision: val
                          ? currentItem.time
                            ? "approximate_datetime"
                            : "approximate_date"
                          : currentItem.time
                          ? "exact_datetime"
                          : "exact_date",
                      });
                    }}
                    label="About"
                    size="small"
                  />
                </View>
              )}

              {/* Formatted Summary Label */}
              <View style={[styles.summaryWrap, activeTarget === "duration" && { maxWidth: "100%" }]}>
                <ThemedText style={[styles.summaryLabel, { color: textMuted }]}>
                  Selection:
                </ThemedText>
                <ThemedText type="defaultSemiBold" style={styles.summaryText} numberOfLines={1}>
                  {pickerMode === "Date"
                    ? fromDate.dateStr || "None"
                    : rangeEndType === "duration" && duration
                    ? `${fromDate.dateStr || "?"} (${duration})`
                    : `${fromDate.dateStr || "?"} to ${toDate.dateStr || "?"}`}
                </ThemedText>
              </View>
            </View>
      </ScrollView>
    </ThemedPopupScreen>
  );
}

const styles = StyleSheet.create({
  hoverCard: {
    position: "absolute",
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.18,
    shadowRadius: 16,
    elevation: 12,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  modalHeaderTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  modalTitleText: {
    fontSize: 14,
  },
  closeBtn: {
    padding: 4,
  },
  scrollBody: {
    flexGrow: 0,
  },
  scrollContent: {
    padding: 7,
    gap: 6,
  },
  modeRow: {
    width: "100%",
  },
  rangeEndTypeRow: {
    width: "100%",
  },
  durationPanel: {
    paddingVertical: 4,
    gap: 6,
  },
  durationSectionTitle: {
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.5,
  },
  durationChipsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  durationChip: {
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  durationChipSelected: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 1,
    elevation: 1,
  },
  durationChipText: {
    fontSize: 11,
    fontWeight: "600",
  },
  customDurationRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  durationInput: {
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    fontSize: 12,
    width: 50,
    textAlign: "center",
  },
  durationUnitChipsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 3,
    flex: 1,
  },
  durationUnitChip: {
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 5,
    paddingVertical: 3,
  },
  durationUnitChipSelected: {
    borderWidth: 1.5,
  },
  durationUnitChipText: {
    fontSize: 10,
    fontWeight: "600",
  },
  calculatedEndBanner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    marginTop: 4,
  },
  calculatedEndText: {
    fontSize: 11,
    flex: 1,
  },
  rangeTargetRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  targetTabBtn: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 6,
    alignItems: "center",
  },
  targetTabBtnActive: {
    borderWidth: 1.5,
  },
  targetLabel: {
    fontSize: 9,
    fontWeight: "700",
    letterSpacing: 0.5,
  },
  targetValue: {
    fontSize: 11,
    marginTop: 1,
  },
  alertBanner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    borderWidth: 1,
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: "rgba(234, 67, 53, 0.08)",
  },
  alertBannerText: {
    fontSize: 11,
    fontWeight: "600",
  },
  dateAndTimePanel: {
    flexDirection: "row",
    gap: 8,
    alignItems: "flex-start",
  },
  calendarCol: {
    flex: 1.3,
  },
  monthNavRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  monthNavArrow: {
    padding: 2,
  },
  monthNavTitle: {
    fontSize: 12,
  },
  weekdayRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  weekdayHeader: {
    width: 24,
    textAlign: "center",
    fontSize: 9,
    fontWeight: "600",
  },
  calendarGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: 1,
  },
  dayCell: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  dayCellSelected: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  dayCellText: {
    fontSize: 10,
    fontWeight: "500",
  },
  dayCellTextSelected: {
    color: "#FFFFFF",
    fontWeight: "700",
  },
  yearAdjustRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 4,
    paddingTop: 2,
  },
  yearChip: {
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 5,
    paddingVertical: 1,
  },
  yearChipText: {
    fontSize: 9,
    fontWeight: "600",
  },
  currentYearText: {
    fontSize: 10,
  },
  clockCol: {
    flex: 1,
    alignItems: "center",
    borderLeftWidth: 1,
    paddingLeft: 6,
  },
  timeHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    marginBottom: 4,
  },
  timeDigitBox: {
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
    minWidth: 26,
    alignItems: "center",
  },
  timeDigitBoxActive: {
    borderWidth: 1.5,
  },
  timeDigitText: {
    fontSize: 12,
  },
  timeColon: {
    fontSize: 12,
    fontWeight: "700",
  },
  amPmCol: {
    flexDirection: "row",
    gap: 2,
    marginLeft: 2,
  },
  amPmBtn: {
    paddingHorizontal: 3,
    paddingVertical: 2,
    borderRadius: 4,
  },
  amPmBtnActive: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 1,
    elevation: 1,
  },
  amPmText: {
    fontSize: 8,
    fontWeight: "700",
  },
  amPmTextActive: {
    color: "#FFFFFF",
  },
  clockDialWrap: {
    width: CLOCK_DIAMETER,
    height: CLOCK_DIAMETER,
    position: "relative",
    justifyContent: "center",
    alignItems: "center",
  },
  clockNumberWrap: {
    position: "absolute",
    width: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: "center",
    alignItems: "center",
  },
  clockNumberText: {
    fontSize: 8,
    fontWeight: "600",
  },
  clockNumberTextSelected: {
    color: "#FFFFFF",
    fontWeight: "700",
  },
  bottomControlBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderTopWidth: 1,
    paddingTop: 6,
    marginTop: 2,
  },
  approxToggleRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  summaryWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    maxWidth: "60%",
  },
  summaryLabel: {
    fontSize: 10,
  },
  summaryText: {
    fontSize: 11,
  },
  modalFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderTopWidth: 1,
  },
  actionBtn: {
    minWidth: 64,
  },
});

export default DateSelector;
